import React, { useEffect, useRef } from "react";
import AddMobileStep from "./AddMobileStep";
import AuthenticatorCard from "./AuthenticatorCard";
import "./security.css";
import { LOGIN_SETUP, SMS_FACTOR } from "../../../config/featureFlags";
import MethodOrderSection from "./MethodOrderSection";
import SmsFactorCard from "./SmsFactorCard";
import ConfirmActionModal from "./ConfirmActionModal";
import EnrollAuthenticatorModal from "./EnrollAuthenticatorModal";
import StepUpModal from "./StepUpModal";
import useAuthenticatorStatus from "./useAuthenticatorStatus";
import useSecurityFlows from "./useSecurityFlows";
import useStepUp from "./useStepUp";

const CONFIRMATIONS = {
  disable: {
    title: "Disable Authenticator App?",
    body: "You will need to set up your authenticator app again to use it.",
    confirmLabel: "Yes, disable",
  },
  reconfigure: {
    title: "Reconfigure Authenticator App?",
    body: "Your existing authenticator will stay active until the new one is confirmed.",
    confirmLabel: "Yes, reconfigure",
  },
};

// startSetup: the post-login nudge's "Set up now" lands here and opens the existing setup once, as if the user had
// pressed "Set up Authenticator App". An ACTIVE authenticator is left alone (adding another is Reconfigure).
const SecuritySettings = ({ onSignInAgain, startSetup = false, onStartSetupHandled }) => {
  const authenticator = useAuthenticatorStatus();
  const stepUp = useStepUp({ hasTotp: authenticator.status === "ACTIVE" });
  const flows = useSecurityFlows({ stepUp, refresh: authenticator.refresh, isActive: authenticator.status === "ACTIVE" });
  const confirmation = flows.confirm ? CONFIRMATIONS[flows.confirm.kind] : null;
  const startedRef = useRef(false);
  const { loading, status } = authenticator;
  const { setup } = flows;
  useEffect(() => {
    if (!startSetup || startedRef.current || loading) return;
    startedRef.current = true;
    onStartSetupHandled?.();
    if (status && status !== "ACTIVE") setup();
  }, [startSetup, loading, status, setup, onStartSetupHandled]);
  const signInRequired = authenticator.signInRequired || flows.signInRequired;
  const signInMessage = authenticator.error || flows.notice;
  // docs/specs/sms-factor.md "The flag": off, or no `sms` from node yet, renders today's page unchanged.
  const sms = SMS_FACTOR ? authenticator.sms : null;

  return (
    <section className="p-4 mfa-security">
      <h1>Security</h1>
      <p className="text-muted">Ways to prove it is you when you sign in.</p>
      <div>
        {signInRequired ? (
          <div className="alert alert-danger" role="alert">
            {signInMessage} <button type="button" onClick={onSignInAgain}>Sign in again</button>
          </div>
        ) : (
          <AuthenticatorCard
            status={authenticator.status}
            loading={authenticator.loading}
            error={authenticator.error}
            onSetup={flows.setup}
            onReconfigure={() => flows.ask("reconfigure")}
            onDisable={() => flows.ask("disable")}
            onRetry={authenticator.refresh}
            recommended={Boolean(sms)}
          />
        )}
        {!signInRequired && sms && <SmsFactorCard status={sms.status} phoneMasked={sms.phone_masked} />}
        {!signInRequired && LOGIN_SETUP && <MethodOrderSection onSignInAgain={onSignInAgain} />}
      </div>
      {!signInRequired && (
        <>
          {flows.notice && <div className="alert alert-danger" role="alert">{flows.notice}</div>}
          <StepUpModal
            open={stepUp.open}
            method={stepUp.method}
            methods={stepUp.methods}
            busy={stepUp.busy}
            error={stepUp.error}
            onSelectMethod={stepUp.selectMethod}
            onSubmit={stepUp.submit}
            onCancel={stepUp.cancel}
            onResend={stepUp.resend}
          />
          {confirmation && (
            <ConfirmActionModal
              open
              title={confirmation.title}
              body={confirmation.body}
              confirmLabel={confirmation.confirmLabel}
              onConfirm={flows.runConfirmed}
              onCancel={flows.cancelConfirm}
            />
          )}
          {flows.enrollment && (
            <EnrollAuthenticatorModal
              enrollment={flows.enrollment}
              busy={flows.enrollBusy}
              error={flows.enrollError}
              onConfirm={flows.confirmEnrollment}
              onCancel={flows.cancelEnrollment}
              onRestart={flows.restartEnrollment}
            />
          )}
          {flows.mobileStep && <AddMobileStep onDone={flows.finishMobileStep} />}
        </>
      )}
    </section>
  );
};

export default SecuritySettings;
